import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { http, formatApiError, reportingWeek, formatWeek, formatMinutes } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, FileEdit, CheckCircle2, Clock, MapPin, Home, Handshake, FileSignature, ArrowRight } from "lucide-react";

export default function CollaboratorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentWeek = reportingWeek();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/reports/me");
        setReports(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentReport = reports.find((r) => r.week_start === currentWeek);
  const totals = reports.reduce(
    (acc, r) => {
      acc.visites += r.visites || 0;
      acc.prospection_minutes += r.prospection_minutes || 0;
      acc.relances_vendeurs += r.relances_vendeurs || 0;
      return acc;
    },
    { visites: 0, prospection_minutes: 0, relances_vendeurs: 0 }
  );

  return (
    <Layout>
      <div className="mb-10" data-testid="collaborator-dashboard">
        <p className="label-eyebrow mb-2">Semaine du {formatWeek(currentWeek)}</p>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">
          Bonjour {user?.name?.split(" ")[0]}
        </h1>
        <p className="mt-3 text-slate-600 text-base max-w-2xl">
          Prêt pour la réunion de lundi&nbsp;? Remplissez votre point hebdomadaire pour partager votre activité avec votre équipe.
        </p>
      </div>

      {/* CTA card */}
      <Card
        className={`p-6 sm:p-8 mb-8 border ${currentReport ? "bg-emerald-50 border-emerald-200" : "bg-primary/5 border-primary/20"}`}
        data-testid="current-week-cta"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-start gap-4">
            <div
              className={`h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                currentReport ? "bg-emerald-600 text-white" : "bg-primary text-primary-foreground"
              }`}
            >
              {currentReport ? <CheckCircle2 className="h-6 w-6" /> : <FileEdit className="h-6 w-6" />}
            </div>
            <div>
              <div className="label-eyebrow mb-1">Cette semaine</div>
              <div className="font-display font-bold text-xl sm:text-2xl">
                {currentReport ? "Rapport transmis" : "Point hebdomadaire à remplir"}
              </div>
              <div className="text-slate-600 text-sm mt-1">
                {currentReport
                  ? "Vous pouvez le modifier jusqu'à la réunion."
                  : "Cela vous prendra environ 2 minutes."}
              </div>
            </div>
          </div>
          <Button size="lg" onClick={() => navigate("/rapport")} data-testid="fill-report-button" className="gap-2">
            {currentReport ? "Modifier" : "Remplir maintenant"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </Card>

      {/* Personal totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatBlock icon={Home} label="Visites totales" value={totals.visites} tone="blue" />
        <StatBlock icon={Clock} label="Prospection cumulée" value={formatMinutes(totals.prospection_minutes)} tone="amber" />
        <StatBlock icon={MapPin} label="Relances vendeurs" value={totals.relances_vendeurs} tone="emerald" />
        <StatBlock icon={FileSignature} label="Rapports transmis" value={reports.length} tone="slate" />
      </div>

      {/* History */}
      <div className="mb-4 flex items-end justify-between">
        <div>
          <p className="label-eyebrow mb-1">Historique</p>
          <h2 className="font-display font-bold text-2xl">Mes semaines précédentes</h2>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : reports.length === 0 ? (
        <Card className="p-12 text-center text-slate-500" data-testid="empty-history">
          Aucun rapport pour le moment. Commencez par remplir celui de cette semaine.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reports.map((r) => (
            <Card key={r.id} className="p-5 hover:shadow-md transition-all duration-200" data-testid={`report-card-${r.week_start}`}>
              <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                  <div className="label-eyebrow mb-0.5">Semaine du</div>
                  <div className="font-display font-semibold text-lg">{formatWeek(r.week_start)}</div>
                </div>
                <Badge variant="secondary" className="capitalize">
                  {r.prospection_type === "aucune" ? "—" : r.prospection_type}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <MiniStat label="Visites" value={r.visites} icon={Home} />
                <MiniStat label="Prospection" value={formatMinutes(r.prospection_minutes)} icon={Clock} />
                <MiniStat label="Relances" value={r.relances_vendeurs} icon={MapPin} />
                <MiniStat label="Compromis" value={r.compromis_en_cours} icon={Handshake} />
              </div>
            </Card>
          ))}
        </div>
      )}
    </Layout>
  );
}

function StatBlock({ icon: Icon, label, value, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    slate: "bg-slate-100 text-slate-700 ring-slate-200",
  };
  return (
    <Card className="p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ring-1 ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
          <div className="font-display font-bold text-2xl text-slate-900 truncate">{value}</div>
        </div>
      </div>
    </Card>
  );
}

function MiniStat({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 text-slate-700">
      <Icon className="h-4 w-4 text-slate-400" />
      <span className="text-slate-500">{label}</span>
      <span className="ml-auto font-semibold text-slate-900">{value}</span>
    </div>
  );
}
